import { useEffect, useState } from 'react';
import { useMedia } from 'react-media';

import { BreakpointKeys, FormFactors } from '#/service/constants';

/**
 * Custom hook that helps customize component's behaviour depending on the browser's width.
 * @example const { isMobile } = useResponsiveValue<{ isMobile: boolean; }>({
  [BreakpointKeys.XS]: { isMobile: true },
  [BreakpointKeys.SM]: { isMobile: true },
  ...
});
 * @param breakpointMap {object} Mapping object between breakpoints and desired behaviour
 * @param mediaQueries {object} Custom media queries. If not provided, the default ones are used.
 * @returns `{T}` The most appropriate settings for the given browser's width
 */
export function useResponsiveValue<T>(
  breakpointMap: BreakpointMap<T>,
  mediaQueries = MEDIA_QUERIES
): T {
  const [result, setResult] = useState<T>(breakpointMap[BreakpointKeys.XL]);
  const matches = useMedia({ queries: mediaQueries });

  useEffect(() => {
    const typedKeys = Object.keys(matches) as BreakpointKeys[];
    const breakpoint = typedKeys.find((key) => matches[key] === true);

    if (breakpoint) {
      setResult(breakpointMap[breakpoint]);
    }
  }, [matches, breakpointMap]);

  return result;
}

/**
 * Convenient wrapper for using `useResponsiveValue` for just a form factor.
 */
export function useResponsiveFormFactor() {
  return useResponsiveValue<FormFactors>(breakpointToFormFactorMap);
}

/**
 * Convenient wrapper for using `useResponsiveValue` for just a standardized
 * `isMobile` boolean, including both phones and tablets.
 */
export function useIsMobile() {
  return useResponsiveValue<boolean>(isMobileBreakpointMap);
}

/**
 * Convenient wrapper for using `useResponsiveValue` for just a standardized
 * `isPhone` boolean, excluding tablets.
 */
export function useIsPhone() {
  return useResponsiveValue<boolean>(isPhoneBreakpointMap);
}

/**
 * Returns the current form factor of the browser.
 *
 * A simplified version of `useResponsiveFormFactor` that doesn't require a React context or event listeners.
 * @returns The current form factor of the browser window, or UNKNOWN if it can't be determined.
 */
export const getWindowFormFactor = (): Uppercase<FormFactors> | 'UNKNOWN' => {
  const matchedQuery = Object.entries(MEDIA_QUERIES).find(
    ([_, mediaQuery]) => window.matchMedia(mediaQuery).matches
  );

  if (matchedQuery) {
    const breakpointKey = matchedQuery[0] as BreakpointKeys;

    return breakpointToFormFactorMap[breakpointKey].toUpperCase();
  }

  return 'UNKNOWN';
};

export const breakpoints = {
  // xxl: null,
  [BreakpointKeys.XL]: 1600,
  [BreakpointKeys.LG]: 1200,
  [BreakpointKeys.MD]: 992,
  [BreakpointKeys.SM]: 768,
  [BreakpointKeys.XS]: 576,
};

export const MEDIA_QUERIES: Record<BreakpointKeys, string> = {
  [BreakpointKeys.XS]: `(max-width: ${breakpoints[BreakpointKeys.XS]}px)`,
  [BreakpointKeys.SM]: `(max-width: ${breakpoints[BreakpointKeys.SM]}px)`,
  [BreakpointKeys.MD]: `(max-width: ${breakpoints[BreakpointKeys.MD]}px)`,
  [BreakpointKeys.LG]: `(max-width: ${breakpoints[BreakpointKeys.LG]}px)`,
  [BreakpointKeys.XL]: `(max-width: ${breakpoints[BreakpointKeys.XL]}px)`,
  [BreakpointKeys.XXL]: `(min-width: ${breakpoints[BreakpointKeys.XL] + 1}px)`,
};

export type BreakpointMap<T> = {
  [key in BreakpointKeys]: T;
};

/** "mobile" means phones and tablets. */
const isMobileBreakpointMap = {
  [BreakpointKeys.XS]: true,
  [BreakpointKeys.SM]: true,
  [BreakpointKeys.MD]: false,
  [BreakpointKeys.LG]: false,
  [BreakpointKeys.XL]: false,
  [BreakpointKeys.XXL]: false,
};

/** "phone" means phones but not tablets. */
const isPhoneBreakpointMap = {
  [BreakpointKeys.XS]: true,
  [BreakpointKeys.SM]: false,
  [BreakpointKeys.MD]: false,
  [BreakpointKeys.LG]: false,
  [BreakpointKeys.XL]: false,
  [BreakpointKeys.XXL]: false,
};

/**
 * Mapping object between breakpoints and form factors for responsive design
 */
export const breakpointToFormFactorMap = {
  [BreakpointKeys.XS]: FormFactors.MOBILE,
  [BreakpointKeys.SM]: FormFactors.TABLET,
  [BreakpointKeys.MD]: FormFactors.TABLET,
  [BreakpointKeys.LG]: FormFactors.TABLET,
  [BreakpointKeys.XL]: FormFactors.DESKTOP,
  [BreakpointKeys.XXL]: FormFactors.DESKTOP,
};

export interface FormFactor {
  formFactor: FormFactors;
}
